import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AuditResultFilter,
  PagedAuditResult,
  getAuditResults,
  getAuditResultsCsv,
} from 'core/application/csiDashboard/auditSearch/getAuditResults'

import { downloadCSV } from 'common/downloadFile'
import { ROUTE_PATHS } from 'common/routePaths'

import { formatMoney } from '../../common'
import { Card, Col, Row } from '../../common/components'
import TableV2 from '../../common/components/dataDisplay/TableV2'
import { formatDataAsDashedV2, nowV2, parseZoneFormatV2 } from '../../common/dateFormatters'
import { useQueryRequestV2 } from '../../common/hooks'
import { EXPENSES_PATH } from '../../docReviewDashboard/Expenses'
import SearchFields from './SearchFields'

export const AUDIT_SEARCH_PATH = 'audit-search'

const defaultFilter: AuditResultFilter = {
  pageNumber: 1,
  pageSize: 10,
  orderColumn: 'expenseNumber',
  orderDescending: false,
}

const AuditSearch = () => {
  const { t: translate } = useTranslation()

  const [searchFilter, setSearchFilter] = useState<AuditResultFilter | null>(null)

  const { sendQuery, isLoading, data: auditResults } = useQueryRequestV2<PagedAuditResult>()
  const { sendQuery: sendCsvQuery, isLoading: isCsvLoading } = useQueryRequestV2<string>()

  useEffect(() => {
    ;(async () => {
      if (searchFilter) {
        await sendQuery(() => getAuditResults(searchFilter))
      }
    })()
  }, [searchFilter])

  const onSearch = (searchReq: any) => {
    const resolvedSearchFilter = searchFilter ?? defaultFilter

    setSearchFilter({
      ...resolvedSearchFilter,
      company: searchReq.company,
      docReviewer: searchReq.docReviewer,
      employee: searchReq.employee,
      startDate: searchReq.startDate,
      endDate: searchReq.endDate,
      pageNumber: 1,
    })
  }

  const onDownload = async (searchReq: any) => {
    const result = await sendCsvQuery(() =>
      getAuditResultsCsv({
        company: searchReq.company,
        docReviewer: searchReq.docReviewer,
        employee: searchReq.employee,
        startDate: searchReq.startDate,
        endDate: searchReq.endDate,
        pageNumber: 1,
        pageSize: -1,
        orderColumn: searchFilter?.orderColumn ?? defaultFilter.orderColumn,
        orderDescending: searchFilter?.orderDescending ?? defaultFilter.orderDescending,
      }),
    )

    downloadCSV(result, `audit_search_${formatDataAsDashedV2(searchReq.startDate ?? nowV2())}.csv`)
  }

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    const resolvedSearchFilter = searchFilter ?? defaultFilter

    setSearchFilter({
      ...resolvedSearchFilter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field ?? resolvedSearchFilter.orderColumn,
      orderDescending: sorter.order === 'descend',
    })
  }

  const columns: any[] = [
    {
      title: 'Record Number',
      dataIndex: 'expenseNumber',
      key: 'expenseNumber',
      sorter: false,
      align: 'center',
      render: (expenseNumber: string, record: any) => (
        <a href={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${EXPENSES_PATH}/${record.expenseId}`} target="_blank">
          {expenseNumber}
        </a>
      ),
    },
    {
      title: 'CompanyId',
      dataIndex: 'companyId',
      key: 'companyId',
      sorter: false,
    },
    {
      title: 'UserId',
      dataIndex: 'userId',
      key: 'userId',
      sorter: false,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: false,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      sorter: false,
      render: (amount: number, record: any) => formatMoney(amount),
    },
    {
      title: 'Reviewed On',
      dataIndex: 'reviewedOn',
      key: 'reviewedOn',
      sorter: false,
      render: (reviewedOn: string, record: any) => <span>{reviewedOn && parseZoneFormatV2(reviewedOn)}</span>,
    },
    {
      title: 'Submitted On',
      dataIndex: 'submittedOn',
      key: 'submittedOn',
      sorter: false,
      render: (submittedOn: string, record: any) => <span>{submittedOn && parseZoneFormatV2(submittedOn)}</span>,
    },
    {
      title: 'Reviewed By',
      dataIndex: 'reviewedBy',
      key: 'reviewedBy',
      sorter: false,
    },
    {
      align: 'center',
      title: 'Plan Year',
      dataIndex: 'planYear',
      key: 'planYear',
      sorter: false,
    },
  ]

  return (
    <Card title="Audit Search">
      <Row>
        <Col span={24}>
          <SearchFields
            isSearchButtonLoading={isLoading}
            isDownloadButtonLoading={isCsvLoading}
            onSearch={onSearch}
            onDownload={onDownload}
          ></SearchFields>
        </Col>
      </Row>

      {auditResults && (
        <Row>
          <Col span={24}>
            <h5 className="mt-16">
              {auditResults.totalItemCount} {translate('csi-dashboard.audit-search.search-results')}
            </h5>
          </Col>

          <Col span={24}>
            <TableV2
              dataSource={auditResults.items}
              loading={isLoading}
              onChange={onTableChange}
              columns={columns}
              pagination={{
                total: auditResults.totalItemCount,
                pageSize: auditResults.pageSize,
                current: auditResults.pageNumber,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '30', '40'],
              }}
            ></TableV2>
          </Col>
        </Row>
      )}
    </Card>
  )
}

export default AuditSearch
